import { createContext, useContext, useMemo } from 'react';

import { type NoteOutcome } from '@apps/web/lib/agent_note_outcome_copy';
import { type AgentNoteRead } from '@libraries/typescript/api_client';

export type SavedNoteOutcomes = {
    // The notes themselves, so the Learned tab reads what the provider already
    // fetched instead of running the same poll a second time.
    readonly notes: ReadonlyArray<AgentNoteRead>;
    readonly outcomesByNoteId: ReadonlyMap<string, ReadonlyArray<NoteOutcome>>;
    // Refetch the notes poll now and resume polling until the note settles
    // again — used after approving or rejecting a proposal. Await it before
    // re-enabling controls so stale buttons cannot double-submit.
    readonly refresh: () => Promise<void>;
};

const EMPTY: SavedNoteOutcomes = {
    notes: [],
    outcomesByNoteId: new Map(),
    refresh: async () => undefined,
};

/**
 * Resolved destinations for the notes in this transcript, keyed by note id.
 *
 * A context rather than a prop because the chip is rendered from deep inside
 * the timeline switch in two separate surfaces; threading a map through both
 * would touch far more code than the feature is worth. A note absent from the
 * map has not settled yet, which the chip renders as its plain self.
 */
export const SavedNoteOutcomesContext = createContext<SavedNoteOutcomes>(EMPTY);

export function useSavedNoteOutcomes(
    noteId: string,
): ReadonlyArray<NoteOutcome> {
    return (
        useContext(SavedNoteOutcomesContext).outcomesByNoteId.get(noteId) ?? []
    );
}

/** Every note in this transcript, as already fetched by the provider. */
export function useSavedNotes(): ReadonlyArray<AgentNoteRead> {
    return useContext(SavedNoteOutcomesContext).notes;
}

/** Refetch the transcript's notes poll immediately. */
export function useRefreshSavedNotes(): () => Promise<void> {
    return useContext(SavedNoteOutcomesContext).refresh;
}

export type ProposalOutcome = {
    readonly noteId: string;
    readonly outcome: NoteOutcome;
};

/**
 * Every staged proposal across the transcript's notes, in note order. The
 * approval dock is the single surface that renders these; the transcript
 * chips deliberately skip them.
 */
export function useProposalOutcomes(): ReadonlyArray<ProposalOutcome> {
    const { outcomesByNoteId } = useContext(SavedNoteOutcomesContext);
    // Map insertion order is note order (the provider builds it from the
    // fetched note list), so proposals dock oldest-first.
    return useMemo(() => {
        const proposals: Array<ProposalOutcome> = [];
        for (const [noteId, outcomes] of outcomesByNoteId) {
            for (const outcome of outcomes) {
                if (outcome.isProposal) {
                    proposals.push({ noteId, outcome });
                }
            }
        }
        return proposals;
    }, [outcomesByNoteId]);
}
